import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import type {
  Company,
  CreateCompanyPayload,
} from "../types/companies.types";
import styles from "./CompanyModal.module.scss";

const companyFormSchema = z.object({
  name: z
    .string()
    .min(2, "companyModal.validation.nameMin")
    .max(200, "companyModal.validation.nameMax"),
  code: z
    .string()
    .max(50, "Kod en fazla 50 karakter olabilir")
    .optional(),
});

export interface CompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateCompanyPayload) => void;
  editCompany?: Company | null;
  title?: string;
}

const CompanyModal: React.FC<CompanyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editCompany = null,
  title,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const result = companyFormSchema.safeParse({
      name: name.trim(),
      code: code.trim() || undefined,
    });
    setIsFormValid(result.success);
  }, [name, code]);

  useEffect(() => {
    if (isOpen) {
      setFieldErrors({});
      if (editCompany) {
        setName(editCompany.name ?? "");
        setCode(editCompany.code ?? "");
      } else {
        setName("");
        setCode("");
      }
    }
  }, [isOpen, editCompany]);

  const validateForm = () => {
    const result = companyFormSchema.safeParse({
      name: name.trim(),
      code: code.trim() || undefined,
    });
    if (result.success) {
      setFieldErrors({});
      return true;
    }
    const flattened = result.error.flatten().fieldErrors;
    const errors: Record<string, string> = {};
    if (flattened.name?.[0]) errors.name = flattened.name[0];
    if (flattened.code?.[0]) errors.code = flattened.code[0];
    setFieldErrors(errors);
    return false;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit({
      name: name.trim(),
      code: code.trim() || undefined,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title ?? t("companies.addTitle")}
      size="lg"
      withForm
      formId="companyForm"
      handleSubmit={handleSubmit}
      isFormValid={isFormValid}
      submitButtonText={t("buttons.save")}
    >
      <div className={styles.form}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>1</span>
            <div>
              <h4 className={styles.sectionTitle}>
                {t("companies.sectionBasic")}
              </h4>
              <p className={styles.sectionSubtitle}>
                {t("companies.sectionBasicHint")}
              </p>
            </div>
          </div>
          <div className={styles.grid2}>
            <Input
              label={t("companies.name")}
              placeholder={t("companies.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (fieldErrors.name)
                  setFieldErrors((p) => ({ ...p, name: "" }));
              }}
              error={!!fieldErrors.name}
              errorMessage={fieldErrors.name}
              maxLength={200}
              required
            />
            <Input
              label={t("companies.code")}
              placeholder={t("companies.codePlaceholder")}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (fieldErrors.code)
                  setFieldErrors((p) => ({ ...p, code: "" }));
              }}
              error={!!fieldErrors.code}
              errorMessage={fieldErrors.code}
              maxLength={50}
            />
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default CompanyModal;
